import React, {
  useEffect,
  useState,
  useLayoutEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col } from 'react-bootstrap'
import { IQuestionType } from 'models/questionTypes.model'
import { IMainState } from 'store/states'
import * as fromQuestionTypesActions from 'store/actions/questionsTypes.action'
import SelectInput from 'components/ui/forms/field/select-input/SelectInput'
import {
  prepareLikertQuestionData,
  prepareOpenEndedData,
} from 'helpers/converters.helper'
import CheckBoxRadioButtonInput from 'components/ui/forms/field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import { QuestionTypeV1 } from 'enum/question-type'
import Loader, { LoaderSizes } from 'components/ui/loader/Loader'
import { IQuestionData } from 'models/question.model'
import { ILikerOptionModel } from 'models/questionLikert.model'
import RequiredFieldLabel from 'components/reusables/requiredFieldLabel'
import LikertQuestion from './components/likert-question/LikertQuestion'
import styles from './SurveyQuestionsEditor.module.scss'

interface ISurveyQuestionsEditor {
  index: number
  data?: IQuestionData
  blockId: number | undefined
  onSave: any
  id?: number
}

export interface SurveyQuestionsEditorHandle {
  save: () => void
}

const questionDefaultValues = {
  name: '',
  mandatory: false,
  type: { value: '', label: '' },
}

const likertAnswerDefaultValues: ILikerOptionModel[] = [
  { label: '' },
  { label: '' },
  { label: '' },
  { label: '' },
  { label: '' },
]

/** Fields of one survey question. The question modal owns discard and save. */
const SurveyQuestionsEditor = forwardRef<
  SurveyQuestionsEditorHandle,
  ISurveyQuestionsEditor
>(function SurveyQuestionsEditor(
  { data = undefined, index, blockId, onSave },
  ref
) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { types } = useSelector(
    ({ questionTypes }: IMainState) => questionTypes
  )
  const { loading } = useSelector(({ questions }: IMainState) => questions)

  const [questionData, setQuestionData] = useState(questionDefaultValues)
  const [answerData, setAnswerData] = useState<ILikerOptionModel[]>(
    likertAnswerDefaultValues
  )

  const [editing, setEditing] = useState(false)
  const [removedLabelIds, setRemovedLabelIds] = useState<number[]>([])

  useLayoutEffect(() => {
    dispatch({ type: fromQuestionTypesActions.GET_QUESTION_TYPES_LIST })
  }, [dispatch])

  const questionTypes = useMemo(
    () =>
      types.map((type: IQuestionType) => {
        return {
          ...type,
          label: t(`administration.surveyEditor.question.${type.label}`),
        }
      }),
    [t, types]
  )

  useEffect(() => {
    if (data) {
      const translateKeys = {
        [QuestionTypeV1.likert]: 'likert',
        [QuestionTypeV1.openEnded]: 'openEnded',
      }

      const questionableType = data.questionableType
      const translateKey = translateKeys[questionableType]
      const prepareData = {
        ...data,
        type: {
          label: t(`administration.surveyEditor.question.${translateKey}`),
          value: questionableType.toString(),
        },
        id: data.questionableId,
      }

      setQuestionData(prepareData)
      setRemovedLabelIds([])
      setAnswerData((current) => {
        const options = [...current]
        data.options.forEach((option: any, index: number) => {
          options[index] = option
        })
        return options
      })
      setEditing(true)
    }
  }, [data, t])

  const handleOnSave = async () => {
    let data: any
    if (questionData['name'] && questionData.type.label !== '') {
      switch (questionData.type.value) {
        case QuestionTypeV1.likert:
          data = prepareLikertQuestionData(
            questionData,
            answerData,
            index,
            blockId,
            removedLabelIds
          )
          await onSave(data, questionData.type.value)
          break
        case QuestionTypeV1.openEnded:
          data = prepareOpenEndedData(questionData, index, blockId)
          await onSave(data, questionData.type.value)
          break
        default:
      }
    }
  }

  useImperativeHandle(ref, () => ({
    save: () => {
      void handleOnSave()
    },
  }))

  useEffect(() => {
    const textarea = document.getElementById('text-area')
    if (textarea) {
      textarea.style.height = '0px'
      const height =
        textarea.scrollHeight > 54
          ? 54
          : textarea.scrollHeight < 40
          ? 40
          : textarea.scrollHeight
      textarea.style.height = height + 'px'
    }
  }, [questionData])

  const handleRemoveSavedLabel = (id: number) =>
    setRemovedLabelIds((current) => [...current, id])

  const fields = questionData && (
    <div>
      <div className={styles.questionText}>
        <RequiredFieldLabel required>
          <label htmlFor="text-area">
            {t('administration.surveyEditor.question.singular')}
          </label>
        </RequiredFieldLabel>
        <textarea
          rows={1}
          name={'name'}
          id="text-area"
          value={questionData['name']}
          aria-required
          className="w-100 form-control"
          style={{ resize: 'none', overflow: 'hidden', height: '40px' }}
          onChange={(e: any) =>
            setQuestionData({ ...questionData, name: e.target.value })
          }
        />
      </div>

      <Row className={'d-flex mb-3 align-items-center'}>
        <Col md={6} lg={5} xl={4} className={styles.questionType}>
          <SelectInput
            name="questionType"
            options={questionTypes}
            closeMenuOnSelect={true}
            value={questionData['type'].value ? questionData['type'] : null}
            placeHolder={t('administration.surveyEditor.question.questionType')}
            isMulti={false}
            withColors={false}
            onChange={(selected: any) =>
              setQuestionData({ ...questionData, type: selected })
            }
            error={''}
            disabled={editing}
            // Above the question modal, which would otherwise clip the menu.
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 2100 }) }}
          />
        </Col>
        <Col>
          <CheckBoxRadioButtonInput
            key={blockId}
            name={'mandatory-true'}
            value={true}
            checked={questionData['mandatory']}
            inline={true}
            type={'checkbox'}
            label={t('administration.surveyEditor.question.mandatoryQuestion')}
            disabled={questionData['type'].value === ''}
            onChange={() =>
              setQuestionData({
                ...questionData,
                mandatory: !questionData['mandatory'],
              })
            }
            error={''}
          />
        </Col>
      </Row>
      {questionData['type'].value !== '' &&
        showSelectedQuestionTypeForm(
          questionData['type'].value,
          answerData,
          setAnswerData,
          handleRemoveSavedLabel
        )}
    </div>
  )

  return loading ? <Loader sizes={LoaderSizes.ExtraSmall} color /> : fields
})

export default SurveyQuestionsEditor

const showSelectedQuestionTypeForm = (
  type: string,
  data: ILikerOptionModel[],
  onChange: any,
  onRemoveSaved: (id: number) => void
) => {
  switch (type) {
    case 'Questions::Likert':
      return (
        <LikertQuestion
          data={data}
          onChange={onChange}
          onRemoveSaved={onRemoveSaved}
        />
      )
    // Las preguntas de tipo Questions::OpenEnded no retornan nada
    default:
      break
  }
}
